/**
 * LocalModelInstallGate
 *
 * Replaces the dead-end for a local AI model that isn't ready yet with an
 * actionable state instead of a guaranteed-fail action:
 *
 *  - Not installed, but this machine CAN run it -> "Install <model> (<size>)"
 *    primary button with REAL progress from useLocalModelInstalls, plus a
 *    secondary link to the full Settings -> Additional Installations manager.
 *  - Installing -> live percent + stage from the backend's install stream.
 *  - Failed install -> the real error message plus a retry.
 *  - Not installed AND this machine can NEVER run it (CUDA-only model on
 *    Apple Silicon, not enough VRAM/RAM/disk) -> the missing requirements
 *    spelled out and NO install offer. Never offers a download that cannot
 *    work (RULE #5). Callers that have a working alternative to suggest pass
 *    it as `incompatibleAction`.
 *
 * This is the ONE gate used by every feature that can hit "the model isn't on
 * disk yet" — sound effects (SfxGenerationPanel) and voice cloning
 * (AudioLanguageSelector) both render this component (RULE #-2).
 *
 * Driven entirely by useLocalModelInstalls's real installed/requirements state
 * (via the `compatible`/`progress` props), never by parsing a backend error
 * string. That is deliberate: it lets the gate work identically before the
 * user's first attempt (proactive) and after a stale failure for a model that
 * is still not installed (reactive), regardless of whether the backend has
 * started attaching structured `details` (component_id, installed,
 * installable_here, requirements) to its error responses.
 *
 * The companion rule lives in the hook: callers must only mount this gate once
 * useLocalModelInstalls reports `stateKnown`, because the installed-set is
 * empty while the request is in flight and stays empty if it fails — acting on
 * it earlier would offer a fresh multi-GB download for a model already on disk.
 */

import { memo, type ReactNode } from 'react';
import { Download, AlertTriangle, RefreshCw, Settings } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import type { LocalModelInstallProgress } from '../../hooks/useLocalModelInstalls';

export interface LocalModelInstallGateProps {
  /** Display name of the model this gate is blocking on. */
  modelName: string;
  /** Download size in MB, 0 when not yet known (requirements still loading). */
  downloadMb: number;
  vramGb: number;
  ramGb: number;
  diskGb: number;
  /** False when this machine's GPU/RAM/disk cannot meet this model's requirements. */
  compatible: boolean;
  /** Live install progress for this model, if an install is in flight or just finished/failed. */
  progress?: LocalModelInstallProgress;
  /** Start (or retry) the real installation via useLocalModelInstalls.installModel(). */
  onInstall: () => void;
  /** One-line explanation of what installing buys the user, in the caller's own words. */
  notInstalledDescription: string;
  /** Rendered under the incompatible-hardware panel (e.g. "use <other model> instead"). */
  incompatibleAction?: ReactNode;
  className?: string;
}

const STAGE_LABELS: Record<
  LocalModelInstallProgress['stage'],
  { key: string; defaultValue: string }
> = {
  preparing: { key: 'localModelGate.stagePreparing', defaultValue: 'Preparing...' },
  downloading: { key: 'localModelGate.stageDownloading', defaultValue: 'Downloading...' },
  extracting: {
    key: 'localModelGate.stageExtracting',
    defaultValue: 'Extracting files...',
  },
  configuring: { key: 'localModelGate.stageConfiguring', defaultValue: 'Configuring...' },
  complete: { key: 'localModelGate.stageComplete', defaultValue: 'Installed!' },
  error: { key: 'localModelGate.stageError', defaultValue: 'Installation failed' },
};

/** "2.1GB" / "512MB" — mirrors the formatting already used by SfxProviderSelector. */
function formatSize(downloadMb: number): string {
  return downloadMb >= 1024 ? `${(downloadMb / 1024).toFixed(1)}GB` : `${Math.round(downloadMb)}MB`;
}

function openInstallationsSettings(): void {
  // Established navigation contract (App.tsx's 'navigate-to-mode' listener):
  // { mode, subTab } deep-links into SettingsPage's Installations sub-tab.
  window.dispatchEvent(
    new CustomEvent('navigate-to-mode', { detail: { mode: 'settings', subTab: 'installations' } })
  );
}

export const LOCAL_MODEL_GATE_PRIMARY_BUTTON_CLASS =
  'w-full px-6 py-4 rounded-lg font-semibold text-white transition-all min-h-[56px] ' +
  'flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-500 active:bg-purple-700';

export const LocalModelInstallGate = memo(function LocalModelInstallGate({
  modelName,
  downloadMb,
  vramGb,
  ramGb,
  diskGb,
  compatible,
  progress,
  onInstall,
  notInstalledDescription,
  incompatibleAction,
  className = '',
}: LocalModelInstallGateProps) {
  const { t } = useTranslation();

  // --- Incompatible hardware: never offer install, say exactly what's missing ---
  if (!compatible) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="p-4 rounded-lg bg-amber-900/20 border border-amber-500/30 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-amber-300 mb-1">
              {t('localModelGate.incompatibleTitle', {
                defaultValue: "{{model}} can't run on this machine",
                model: modelName,
              })}
            </h4>
            <p className="text-sm text-amber-300/80">
              {t('localModelGate.incompatibleDescription', {
                defaultValue:
                  "Requires {{vram}}GB VRAM, {{ram}}GB RAM and {{disk}}GB free disk space, which this machine doesn't meet.",
                vram: vramGb,
                ram: ramGb,
                disk: diskGb,
              })}
            </p>
          </div>
        </div>

        {incompatibleAction}
      </div>
    );
  }

  const isInstalling = !!progress && progress.stage !== 'complete' && progress.stage !== 'error';
  const isError = progress?.stage === 'error';

  // --- Installing: real percent + stage from the backend SSE stream ---
  if (isInstalling && progress) {
    const stage = STAGE_LABELS[progress.stage];
    return (
      <div className={`p-4 rounded-lg bg-blue-900/20 border border-blue-500/30 ${className}`}>
        <div className="flex items-center gap-3 mb-3">
          <Download className="w-5 h-5 text-blue-400 flex-shrink-0 animate-pulse" />
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-blue-300">
              {t('localModelGate.installingTitle', {
                defaultValue: 'Installing {{model}}...',
                model: modelName,
              })}
            </h4>
            <p className="text-xs text-blue-300/70 truncate">
              {t(stage.key, { defaultValue: stage.defaultValue })}
              {progress.message ? ` — ${progress.message}` : ''}
            </p>
          </div>
          <span className="text-sm font-medium text-blue-300 flex-shrink-0">
            {Math.round(progress.percent)}%
          </span>
        </div>
        <div className="h-2 bg-black/30 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 transition-all duration-300 ease-out"
            style={{ width: `${Math.min(100, Math.max(0, progress.percent))}%` }}
          />
        </div>
      </div>
    );
  }

  // --- Install failed: surface the real error + a retry, never a dead end ---
  if (isError && progress) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="p-4 rounded-lg bg-red-900/20 border border-red-500/30 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-red-300 mb-1">
              {t('localModelGate.stageError', { defaultValue: 'Installation failed' })}
            </h4>
            <p className="text-sm text-red-300/80 break-words">{progress.message}</p>
          </div>
        </div>
        <button type="button" onClick={onInstall} className={LOCAL_MODEL_GATE_PRIMARY_BUTTON_CLASS}>
          <RefreshCw className="w-5 h-5" />
          <span>{t('localModelGate.retryButton', { defaultValue: 'Retry Install' })}</span>
        </button>
      </div>
    );
  }

  // --- Not installed yet, compatible: the actionable install offer ---
  const sizeSuffix = downloadMb > 0 ? ` (${formatSize(downloadMb)})` : '';

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="p-4 rounded-lg bg-purple-900/20 border border-purple-500/30 flex items-start gap-3">
        <Download className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-purple-300 mb-1">
            {t('localModelGate.notInstalledTitle', {
              defaultValue: '{{model}} is not installed',
              model: modelName,
            })}
          </h4>
          <p className="text-sm text-purple-300/80">{notInstalledDescription}</p>
        </div>
      </div>

      <button type="button" onClick={onInstall} className={LOCAL_MODEL_GATE_PRIMARY_BUTTON_CLASS}>
        <Download className="w-5 h-5" />
        <span>
          {t('localModelGate.installButton', {
            defaultValue: 'Install {{model}}{{size}}',
            model: modelName,
            size: sizeSuffix,
          })}
        </span>
      </button>

      <button
        type="button"
        onClick={openInstallationsSettings}
        className="w-full text-center text-xs text-white/50 hover:text-white/70 transition-colors underline underline-offset-2 min-h-[44px] flex items-center justify-center gap-1.5"
      >
        <Settings className="w-3.5 h-3.5" />
        <span>
          {t('localModelGate.advancedLink', {
            defaultValue: 'Manage in Settings - Additional Installations',
          })}
        </span>
      </button>
    </div>
  );
});

export default LocalModelInstallGate;
